/**
 * Facility readiness checklist (from TechForce Robotics Robot Installation Checklist).
 * `line` = document has a fill-in line to the right of the check; `checkbox` = standard check only.
 */

export type SalesChecklistEntry = 'checkbox' | 'line';

export type SalesChecklistItem = {
  id: string;
  label: string;
  entry: SalesChecklistEntry;
  /** Only when entry === 'line' */
  inputType?: 'text' | 'number';
};

export type SalesChecklistSection = {
  id: string;
  title: string;
  items: SalesChecklistItem[];
};

/** Sections 1, 2, 5, 7, 8 — may include multiple photo attachments on the order page. */
export const SALES_CHECKLIST_SECTION_IDS_WITH_PHOTOS: readonly string[] = [
  'power-electrical',
  'beverage-keg',
  'pos',
  'branding-marketing',
  'facility-layout',
];

export function salesChecklistSectionAllowsPhotos(sectionId: string): boolean {
  return SALES_CHECKLIST_SECTION_IDS_WITH_PHOTOS.includes(sectionId);
}

export const SALES_CHECKLIST_SECTIONS: SalesChecklistSection[] = [
  {
    id: 'power-electrical',
    title: '1. Power & Electrical Requirements',
    items: [
      { id: 'power-dedicated-outlet', label: 'Dedicated electrical outlet available', entry: 'checkbox' },
      { id: 'power-voltage', label: 'Voltage confirmed (110V / 220V as required)', entry: 'checkbox' },
      { id: 'power-outlet-distance', label: 'Outlet distance from install location (feet)', entry: 'line', inputType: 'number' },
      { id: 'power-surge-breaker', label: 'Surge protector or dedicated breaker installed', entry: 'checkbox' },
      { id: 'power-backup', label: 'Backup power plan (if applicable)', entry: 'checkbox' },
      { id: 'power-internet', label: 'Internet connection confirmed (WiFi / Hardline)', entry: 'checkbox' },
    ],
  },
  {
    id: 'beverage-keg',
    title: '2. Beverage / Keg Requirements',
    items: [
      { id: 'bev-keg-count', label: 'Number of kegs supported', entry: 'line', inputType: 'number' },
      { id: 'bev-storage', label: 'Keg storage location identified', entry: 'checkbox' },
      { id: 'bev-co2', label: 'CO₂ system available and tested', entry: 'checkbox' },
      { id: 'bev-refrigeration', label: 'Refrigeration confirmed', entry: 'checkbox' },
      { id: 'bev-line-length', label: 'Line length from keg to robot measured', entry: 'line', inputType: 'number' },
      { id: 'bev-sanitation', label: 'Cleaning & sanitation schedule established', entry: 'checkbox' },
    ],
  },
  {
    id: 'operational-volume',
    title: '3. Operational Volume',
    items: [
      { id: 'vol-pours-day', label: 'Estimated pours per day', entry: 'line', inputType: 'number' },
      { id: 'vol-peak', label: 'Peak hour volume', entry: 'line' },
      { id: 'vol-tx-time', label: 'Average transaction time goal', entry: 'line' },
      { id: 'vol-events', label: 'Special event volume projections included', entry: 'line' },
    ],
  },
  {
    id: 'hours-operation',
    title: '4. Hours of Operation',
    items: [
      { id: 'hrs-daily', label: 'Daily operating hours', entry: 'line' },
      { id: 'hrs-weekend', label: 'Weekend/event variations', entry: 'line' },
      { id: 'hrs-maintenance', label: 'Maintenance window scheduled', entry: 'line' },
      { id: 'hrs-staff', label: 'Staff coverage aligned with robot operation', entry: 'checkbox' },
    ],
  },
  {
    id: 'pos',
    title: '5. POS Integration',
    items: [
      { id: 'pos-system', label: 'POS system identified', entry: 'line' },
      { id: 'pos-api', label: 'API or integration capability confirmed', entry: 'checkbox' },
      { id: 'pos-payment', label: 'Payment methods accepted', entry: 'checkbox' },
      { id: 'pos-receipt', label: 'Receipt printing or digital receipt setup', entry: 'checkbox' },
      { id: 'pos-inventory', label: 'Inventory tracking integration confirmed', entry: 'checkbox' },
    ],
  },
  {
    id: 'staffing-training',
    title: '6. Staffing & Training',
    items: [
      { id: 'staff-attendant', label: 'Onsite robot attendant assigned', entry: 'checkbox' },
      { id: 'staff-training', label: 'Staff training scheduled', entry: 'checkbox' },
      { id: 'staff-troubleshoot', label: 'Troubleshooting protocol reviewed', entry: 'checkbox' },
      { id: 'staff-cleaning', label: 'Cleaning & daily reset responsibilities assigned', entry: 'checkbox' },
      { id: 'staff-escalation', label: 'Escalation contact list provided', entry: 'checkbox' },
    ],
  },
  {
    id: 'branding-marketing',
    title: '7. Branding & Marketing Support',
    items: [
      { id: 'brand-logo', label: 'Facility logo received (vector preferred)', entry: 'checkbox' },
      { id: 'brand-wrap', label: 'Robot wrap branding approved', entry: 'checkbox' },
      { id: 'brand-screen', label: 'Digital screen graphics uploaded', entry: 'checkbox' },
      { id: 'brand-signage', label: 'Table tents / signage printed', entry: 'checkbox' },
      { id: 'brand-social', label: 'Social media launch assets prepared', entry: 'checkbox' },
      { id: 'brand-email', label: 'Email announcement drafted', entry: 'checkbox' },
      { id: 'brand-opening', label: 'Grand opening activation planned', entry: 'checkbox' },
    ],
  },
  {
    id: 'facility-layout',
    title: '8. Facility Layout & Placement',
    items: [
      { id: 'layout-map', label: 'Map of facility provided', entry: 'checkbox' },
      { id: 'layout-placement', label: 'Robot placement marked on layout', entry: 'checkbox' },
      { id: 'layout-traffic', label: 'Traffic flow analyzed', entry: 'checkbox' },
      { id: 'layout-ada', label: 'ADA compliance reviewed', entry: 'checkbox' },
      { id: 'layout-queue', label: 'Queue space allocated', entry: 'checkbox' },
      { id: 'layout-visibility', label: 'Visibility from entry/high-traffic areas confirmed', entry: 'checkbox' },
    ],
  },
  {
    id: 'final-preinstall',
    title: '9. Final Pre-Install Confirmation',
    items: [
      { id: 'final-walk', label: 'Site walk completed', entry: 'checkbox' },
      { id: 'final-date', label: 'Install date confirmed', entry: 'line' },
      { id: 'final-delivery', label: 'Delivery logistics scheduled', entry: 'checkbox' },
      { id: 'final-contact', label: 'Onsite contact person', entry: 'line' },
      { id: 'final-review', label: 'Post-install performance review scheduled', entry: 'checkbox' },
    ],
  },
];

export type SalesChecklistState = Record<string, string | boolean>;

export function allSalesChecklistItemIds(): string[] {
  return SALES_CHECKLIST_SECTIONS.flatMap((s) => s.items.map((i) => i.id));
}

/** Normalize API JSON into typed state for all known checklist keys (migrates legacy all-string saves). */
export function normalizeSalesChecklistState(raw: unknown): SalesChecklistState {
  const rawObj = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: SalesChecklistState = {};
  for (const section of SALES_CHECKLIST_SECTIONS) {
    for (const item of section.items) {
      const v = rawObj[item.id];
      if (item.entry === 'line') {
        if (typeof v === 'string') out[item.id] = v;
        else if (typeof v === 'boolean' && v) out[item.id] = 'Yes';
        else if (typeof v === 'number' && !Number.isNaN(v)) out[item.id] = String(v);
        else out[item.id] = '';
      } else {
        if (typeof v === 'boolean') out[item.id] = v;
        else if (typeof v === 'string') {
          const t = v.trim().toLowerCase();
          out[item.id] = t === 'true' || t === 'yes' || t === '1' || t === 'y';
        } else out[item.id] = false;
      }
    }
  }
  return out;
}

export function isSalesChecklistSectionComplete(section: SalesChecklistSection, state: SalesChecklistState): boolean {
  return section.items.every((item) => {
    if (item.entry === 'line') return String(state[item.id] ?? '').trim().length > 0;
    return state[item.id] === true;
  });
}
